import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Collapse,
  MenuItem,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Done as DoneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Replay as ReopenIcon,
} from '@mui/icons-material';
import api, { timeEntriesAPI, tasksAPI, projectsAPI, customersAPI } from '../services/api';
import TaskEditDialog from '../components/TaskEditDialog';
import { renderRichHtml } from '../utils/sanitizeHtml';
import { localISODate } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';

const MyTaskListPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myWorkData, setMyWorkData] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [timeEntryData, setTimeEntryData] = useState({
    subtask_comment: '',
    hours: '',
    date: localISODate(),
  });
  const [editingTask, setEditingTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});

  // Filter states
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);

  // Toggle task description expansion
  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Check if user is admin
      const userIsAdmin = user.is_admin === true;
      setIsAdmin(userIsAdmin);

      // Load customers and projects for filters
      const [customersRes, projectsRes] = await Promise.all([
        customersAPI.getAll(),
        projectsAPI.getAll(),
      ]);
      setCustomers(customersRes.data || []);
      setProjects(projectsRes.data || []);

      // Load tasks - admin sees all, user sees own
      await loadMyTasks(userIsAdmin);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyTasks = async (showAll = isAdmin) => {
    try {
      // Admin sees all tasks, regular user sees only their own
      const myWorkResponse = await tasksAPI.getMyWork(showAll);
      setMyWorkData(myWorkResponse.data);
    } catch (error) {
      console.error('Error loading my tasks:', error);
    }
  };

  // Check if task is in progress
  const isTaskInProgress = (task) => {
    return task.status?.order === 1;
  };

  // Handle task completion
  const handleCompleteTask = async (task) => {
    if (task.is_timetracker_only) {
      setSelectedTask(task);
      setTimeEntryData({
        subtask_comment: '',
        hours: '',
        date: localISODate(),
      });
      setCompleteDialogOpen(true);
    } else {
      try {
        // Use global statuses if no project
        const statusResponse = await api.get(`/task-statuses${task.project_id ? `?project_id=${task.project_id}` : ''}`);
        const doneStatus = statusResponse.data.find(s => s.order === 2);
        if (doneStatus) {
          await tasksAPI.update(task.id, { status_id: doneStatus.id });
          loadMyTasks();
        }
      } catch (error) {
        console.error('Error completing task:', error);
      }
    }
  };

  // Handle time entry submission with task completion
  const handleTimeEntrySubmit = async () => {
    if (!selectedTask || !timeEntryData.hours) return;

    try {
      // Create time entry
      await timeEntriesAPI.create({
        task_id: selectedTask.id,
        hours: parseFloat(timeEntryData.hours),
        subtask_comment: timeEntryData.subtask_comment,
        date: timeEntryData.date,
      });

      // Get done status
      const statusResponse = await api.get(`/task-statuses${selectedTask.project_id ? `?project_id=${selectedTask.project_id}` : ''}`);
      const doneStatus = statusResponse.data.find(s => s.order === 2);

      if (doneStatus) {
        await tasksAPI.update(selectedTask.id, { status_id: doneStatus.id });
      }

      setCompleteDialogOpen(false);
      setSelectedTask(null);
      setTimeEntryData({
        subtask_comment: '',
        hours: '',
        date: localISODate(),
      });
      loadMyTasks();
    } catch (error) {
      console.error('Error submitting time entry:', error);
    }
  };

  // Handle starting a task
  const handleStartTask = async (task) => {
    try {
      const statusResponse = await api.get(`/task-statuses${task.project_id ? `?project_id=${task.project_id}` : ''}`);
      const inProgressStatus = statusResponse.data.find(s => s.order === 1);
      if (inProgressStatus) {
        await tasksAPI.update(task.id, { status_id: inProgressStatus.id });
        loadMyTasks();
      }
    } catch (error) {
      console.error('Error starting task:', error);
    }
  };

  // Handle reopening a completed task
  const handleReopenTask = async (task) => {
    try {
      const statusResponse = await api.get(`/task-statuses${task.project_id ? `?project_id=${task.project_id}` : ''}`);
      const inProgressStatus = statusResponse.data.find(s => s.order === 1);
      if (inProgressStatus) {
        await tasksAPI.update(task.id, { status_id: inProgressStatus.id });
        loadMyTasks();
      }
    } catch (error) {
      console.error('Error reopening task:', error);
    }
  };

  // Handle edit task
  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditDialogOpen(true);
  };

  // Handle task updated
  const handleTaskUpdated = () => {
    setEditDialogOpen(false);
    setEditingTask(null);
    loadMyTasks();
  };

  // Format date label
  const formatDateLabel = (dateStr) => {
    if (dateStr === 'no_date') return 'Ohne Datum';
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: '2-digit', month: '2-digit' };
    return date.toLocaleDateString('de-DE', options);
  };

  // Group tasks by date (without week restriction)
  const groupTasksByDate = (tasks) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = localISODate(today);

    const groups = {
      overdue: [],
      today: [],
      completed: [],
      byDate: {}
    };

    tasks.forEach(task => {
      // Completed tasks go to separate lane
      if (task._category === 'completed') {
        groups.completed.push(task);
        return;
      }

      if (!task.due_date) {
        // Tasks without due date go to a special "Ohne Datum" group
        if (!groups.byDate['no_date']) {
          groups.byDate['no_date'] = [];
        }
        groups.byDate['no_date'].push(task);
      } else {
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const dueDateStr = task.due_date;

        if (dueDate < today) {
          groups.overdue.push(task);
        } else if (dueDateStr === todayStr) {
          groups.today.push(task);
        } else {
          if (!groups.byDate[dueDateStr]) {
            groups.byDate[dueDateStr] = [];
          }
          groups.byDate[dueDateStr].push(task);
        }
      }
    });

    // Sort byDate keys
    const sortedDates = Object.keys(groups.byDate)
      .filter(d => d !== 'no_date')
      .sort((a, b) => new Date(a) - new Date(b));

    // Add 'no_date' at the end if exists
    if (groups.byDate['no_date']) {
      sortedDates.push('no_date');
    }

    return { ...groups, sortedDates };
  };

  // Filter projects by selected customer
  const filteredProjects = selectedCustomer === 'all'
    ? projects
    : projects.filter(p => p.customer_id === parseInt(selectedCustomer));

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Build all tasks array from myWorkData (ALL tasks, not just this week)
  const allTasks = [
    ...(myWorkData?.overdue || []).map(task => ({ ...task, _category: 'overdue' })),
    ...(myWorkData?.today || []).map(task => ({ ...task, _category: 'today' })),
    ...(myWorkData?.this_week || []).map(task => ({ ...task, _category: 'this_week' })),
    ...(myWorkData?.later || []).map(task => ({ ...task, _category: 'later' })),
    ...(myWorkData?.no_due_date || []).map(task => ({ ...task, _category: 'no_due_date' })),
    ...(myWorkData?.completed || []).map(task => ({ ...task, _category: 'completed' })),
  ];

  // Apply filters
  const filteredTasks = allTasks.filter(task => {
    // Customer filter
    if (selectedCustomer !== 'all') {
      // Wie customer_name: Projekt-Kunde vor Task-Kunde
      if ((task.effective_customer_id ?? task.customer_id) !== parseInt(selectedCustomer)) return false;
    }
    // Project filter
    if (selectedProject !== 'all') {
      if (task.project_id !== parseInt(selectedProject)) return false;
    }
    return true;
  });

  const groupedTasks = groupTasksByDate(filteredTasks);
  const totalTaskCount = filteredTasks.filter(t => t._category !== 'completed').length;

  // Render task table
  const renderTaskTable = (tasks, showActions = true, isCompletedLane = false) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600, width: '10%' }}>Task-ID</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '30%' }}>Task</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '20%' }}>Kunde</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '20%' }}>Bearbeiter</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '12%' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '8%' }} align="right">{(showActions || isCompletedLane) ? 'Aktionen' : ''}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task) => (
            <React.Fragment key={task.id}>
              <TableRow
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  cursor: 'pointer',
                }}
              >
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color: task.task_id ? '#1e3a5f' : 'text.disabled',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                    }}
                  >
                    {task.task_id || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {task.description && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleTaskExpansion(task.id); }}
                        sx={{ p: 0.5 }}
                      >
                        {expandedTasks[task.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {task.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {task.customer_name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {task.assignee && (
                      <>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                          {(task.assignee.full_name || task.assignee.username || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          {task.assignee.full_name || task.assignee.username}
                        </Typography>
                      </>
                    )}
                    {!task.assignee && <Typography variant="body2" color="text.secondary">-</Typography>}
                  </Box>
                </TableCell>
                <TableCell>
                  {task.status?.name && (
                    <Chip
                      label={task.status.name}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        bgcolor: task.status.color || '#616161',
                        color: 'white'
                      }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  {showActions && (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Task bearbeiten">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                          sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, width: 28, height: 28 }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {isTaskInProgress(task) ? (
                        <Tooltip title="Als erledigt markieren">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleCompleteTask(task); }}
                            sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' }, width: 28, height: 28 }}
                          >
                            <DoneIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="In Bearbeitung setzen">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleStartTask(task); }}
                            sx={{ bgcolor: 'warning.main', color: 'white', '&:hover': { bgcolor: 'warning.dark' }, width: 28, height: 28 }}
                          >
                            <StartIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  {isCompletedLane && (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Wieder öffnen">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleReopenTask(task); }}
                          sx={{ bgcolor: 'info.main', color: 'white', '&:hover': { bgcolor: 'info.dark' }, width: 28, height: 28 }}
                        >
                          <ReopenIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
              {/* Expandable Description Row */}
              {task.description && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedTasks[task.id] ? 1 : 0 }}>
                    <Collapse in={expandedTasks[task.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            wordBreak: 'break-word',
                            lineHeight: 1.6,
                            '& p': { my: 0.5 },
                            '& ul, & ol': { pl: 3, my: 0.5 },
                            '& li': { mb: 0.25 },
                            '& a': { color: 'primary.main' },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: renderRichHtml(task.description)
                          }}
                        />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const hasAnyTasks = groupedTasks.overdue.length > 0 ||
                      groupedTasks.today.length > 0 ||
                      groupedTasks.completed.length > 0 ||
                      groupedTasks.sortedDates.some(d => groupedTasks.byDate[d]?.length > 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary.main">
          {isAdmin ? 'Alle Tasks' : 'Meine Taskliste'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isAdmin ? 'Übersicht aller zugewiesenen Tasks' : 'Alle dir zugewiesenen Tasks im Überblick'}
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Kunde</InputLabel>
            <Select
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setSelectedProject('all'); // Reset project when customer changes
              }}
              label="Kunde"
            >
              <MenuItem value="all">Alle Kunden</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Projekt</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="Projekt"
            >
              <MenuItem value="all">Alle Projekte</MenuItem>
              {filteredProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            label={`${totalTaskCount} offene Tasks`}
            color="primary"
            size="small"
          />
        </Box>
      </Paper>

      {/* Task Lanes */}
      {!hasAnyTasks ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <CompleteIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Keine Tasks gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedCustomer !== 'all' || selectedProject !== 'all'
              ? 'Versuche einen anderen Filter'
              : 'Alle Tasks erledigt!'
            }
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Überfällig Lane */}
          {groupedTasks.overdue.length > 0 && (
            <Paper elevation={0} sx={{ mb: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{
                px: 3,
                py: 1.5,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                  Überfällig
                </Typography>
                <Chip
                  label={groupedTasks.overdue.length}
                  size="small"
                  sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 'bold', height: 22 }}
                />
              </Box>
              {renderTaskTable(groupedTasks.overdue)}
            </Paper>
          )}

          {/* Heute Lane */}
          {groupedTasks.today.length > 0 && (
            <Paper elevation={2} sx={{ mb: 2, overflow: 'hidden' }}>
              <Box sx={{
                px: 3,
                py: 1.5,
                bgcolor: 'warning.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Heute
                </Typography>
                <Chip
                  label={groupedTasks.today.length}
                  size="small"
                  sx={{ bgcolor: 'white', color: 'warning.dark', fontWeight: 'bold', height: 22 }}
                />
              </Box>
              {renderTaskTable(groupedTasks.today)}
            </Paper>
          )}

          {/* Future Dates Lanes */}
          {groupedTasks.sortedDates.map((dateStr) => {
            const tasks = groupedTasks.byDate[dateStr];
            if (!tasks || tasks.length === 0) return null;

            return (
              <Paper key={dateStr} elevation={2} sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{
                  px: 3,
                  py: 1.5,
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                    {formatDateLabel(dateStr)}
                  </Typography>
                  <Chip
                    label={tasks.length}
                    size="small"
                    sx={{ bgcolor: 'grey.300', fontWeight: 'bold', height: 22 }}
                  />
                </Box>
                {renderTaskTable(tasks)}
              </Paper>
            );
          })}

          {/* Erledigt Lane */}
          {groupedTasks.completed.length > 0 && (
            <Paper elevation={2} sx={{ mb: 2, overflow: 'hidden', opacity: 0.6 }}>
              <Box sx={{
                px: 3,
                py: 1.5,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                  Erledigt diese Woche
                </Typography>
                <Chip
                  label={groupedTasks.completed.length}
                  size="small"
                  sx={{ bgcolor: 'grey.400', color: 'white', fontWeight: 'bold', height: 22 }}
                />
              </Box>
              {renderTaskTable(groupedTasks.completed, false, true)}
            </Paper>
          )}
        </>
      )}

      {/* Time Entry Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Zeiteintrag erstellen
          <IconButton onClick={() => setCompleteDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Kunde"
              value={selectedTask?.customer_name || 'Kein Kunde'}
              disabled
              fullWidth
            />
            <TextField
              label="Task"
              value={selectedTask?.name || ''}
              disabled
              fullWidth
            />
            <TextField
              label="Subtask Kommentar"
              value={timeEntryData.subtask_comment}
              onChange={(e) => setTimeEntryData(prev => ({ ...prev, subtask_comment: e.target.value }))}
              multiline
              rows={3}
              fullWidth
              required
            />
            <TextField
              label="Stunden"
              type="number"
              value={timeEntryData.hours}
              onChange={(e) => setTimeEntryData(prev => ({ ...prev, hours: e.target.value }))}
              required
              fullWidth
              inputProps={{ step: 0.25, min: 0 }}
            />
            <TextField
              label="Datum"
              type="date"
              value={timeEntryData.date}
              onChange={(e) => setTimeEntryData(prev => ({ ...prev, date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleTimeEntrySubmit}
            variant="contained"
            color="primary"
            disabled={!timeEntryData.hours || !timeEntryData.subtask_comment}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Edit Dialog */}
      {editDialogOpen && editingTask && (
        <TaskEditDialog
          key={`task-edit-${editingTask?.id}`}
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingTask(null);
          }}
          task={editingTask}
          projectId={editingTask?.project_id}
          onTaskUpdated={handleTaskUpdated}
        />
      )}
    </Container>
  );
};

export default MyTaskListPage;
